import { useQuery } from "@tanstack/react-query";
import { parse as parseBestEffortJsonStream } from "best-effort-json-parser";
import { addDays } from "date-fns";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useLocation, useNavigate } from "react-router";

import type {
  MentionInputDto,
  MessageCreatedEntityDto,
  MessageCreateRequest,
  MessageMultipleProjectsCreateRequest,
  PostTemplateDto,
  SimpleProjectDto,
  SystemGroupDto,
} from "@/api/types";
import { AdminCreatePostFormAudienceSelector } from "@/components/AdminCreatePost/AdminCreatePostFormAudienceSelector";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { CommunityPostReachDetailsModal } from "@/components/CommunityFeed/CommunityPostReachDetailsModal";
import { createMentionProtectedText, restoreMentionsAfterRewrite } from "@/components/CommunityFeed/MentionComposer";
import { CommunityPostStatus } from "@/components/CommunityFeed/types";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { CreatePostAppPreview } from "@/components/CreatePostAppPreview/CreatePostAppPreview";
import type { FormDocument } from "@/components/DocumentInput/useDocumentInput";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FileQuestion01Icon } from "@/components/Icons/FileQuestion01Icon";
import { MessageSmileSquareIcon } from "@/components/Icons/MessageSmileSquareIcon";
import { PencilLineIcon } from "@/components/Icons/PencilLineIcon";
import { Settings04Icon } from "@/components/Icons/Settings04Icon";
import { XCloseIcon } from "@/components/Icons/XCloseIcon";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import type { PollData } from "@/components/Poll/EditPollModal";
import type { FormVideo } from "@/components/VideoInput/useVideoInput";
import { ActionBar } from "@/design-system/ActionBar";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Info } from "@/design-system/Info";
import { Inline } from "@/design-system/Inline";
import { SectionCard } from "@/design-system/SectionCard";
import { Stack } from "@/design-system/Stack";
import type { MessageRouteStates } from "@/helpers/community-feed";
import { canManageEvents } from "@/helpers/permissions";
import { processJsonStream } from "@/helpers/processJsonStream";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadDocument } from "@/hooks/Network/useUploadDocument";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useUploadVideo } from "@/hooks/Network/useUploadVideo";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { canCreateAnnouncementMessage, canCreateStandardMessage } from "@/modules/community-feed/permissions";
import { useConfig } from "@/providers/config/useConfig";
import { communityFeedMutations } from "@/queries/community-feed/mutations";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { generalMutations } from "@/queries/general/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import type { AdminPostAudienceType } from "../../types";
import { toCommunityPostEvent } from "./communityPostEvent";
import { AdminCreatePostFormAIHelper } from "./components/AdminCreatePostFormAIHelper";
import { AdminCreatePostFormEditor } from "./components/AdminCreatePostFormEditor";
import { AdminCreatePostFormScheduleSelector } from "./components/AdminCreatePostFormScheduleSelector";
import { AdminCreatePostFormTemplateSelector } from "./components/AdminCreatePostFormTemplateSelector";
import { AdminCreatePostFormTypeSelector } from "./components/AdminCreatePostFormTypeSelector";
import { AdminCreatePostRedesignForm } from "./components/AdminCreatePostRedesignForm";
import { AdminCreatePostTipsModal } from "./components/AdminCreatePostTipsModal";
import { usePostCopilotHistory } from "./usePostCopilotHistory";

export type AdminCreatePostFormValues = {
  postType?: MessageCreateRequest["type"];
  title: string;
  content: string;
  /** Template tokens and structured mentions form one value; update or clear both fields together. */
  contentTemplate?: string;
  /** Structured targets referenced by `contentTemplate`. */
  mentions?: MentionInputDto[];
  images: FormImage[];
  documents: FormDocument[];
  videos: FormVideo[];
  poll: PollData | undefined;
  audienceType: AdminPostAudienceType | undefined;
  audienceEntityId: string | undefined;
  scheduleDate: Date | undefined;
  isEmailPreviewOnly: boolean;
  hasDate: "yes" | "no" | undefined;
  eventStartsAt: Date | undefined;
  eventEndsAt: Date | undefined;
  eventType: "announcement" | "common" | undefined;
  eventLocation: string;
  eventMaxParticipants: string;
  eventNoParticipantsLimit: boolean;
  eventAllowGuests: boolean;
};

export type AdminCreateMassPostFormValues = Omit<
  AdminCreatePostFormValues,
  | "postType"
  | "audienceType"
  | "audienceEntityId"
  | "hasDate"
  | "eventStartsAt"
  | "eventEndsAt"
  | "eventType"
  | "eventLocation"
  | "eventMaxParticipants"
  | "eventNoParticipantsLimit"
  | "eventAllowGuests"
> & {
  postType?: MessageMultipleProjectsCreateRequest["type"];
  group: SystemGroupDto | undefined;
  projects: SimpleProjectDto[];
};

export type AdminCreatePostSharedFormValues = AdminCreatePostFormValues | AdminCreateMassPostFormValues;

export function AdminCreatePostPage(): React.ReactNode {
  const [createdMessage, setCreatedMessage] = useState<MessageCreatedEntityDto>();
  const [templateInUse, setTemplateInUse] = useState<PostTemplateDto>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelModalOpened, cancelModalHandler] = useBool(false);
  const [isSuccessModalOpened, successModalHandler] = useBool(false);
  const [isTipsModalOpened, tipsModalHandler] = useBool(false);

  const showFlashToast = useFlashToast();
  const { state: locationState }: { state: MessageRouteStates["adminCreatePost"] } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();
  const sessionUser = useSessionUser();
  const canCreateAnnouncementPost = hasPermission(canCreateAnnouncementMessage);
  const canCreateStandardPost = hasPermission(canCreateStandardMessage);
  const canCreateEvent = hasPermission(canManageEvents);
  const useNewUnifiedPostCreation = useConfig("useNewUnifiedPostCreation");
  const useRedesignedForm = useNewUnifiedPostCreation && canCreateAnnouncementPost;
  let allowedPostTypes: MessageCreateRequest["type"][] = [];
  if (canCreateAnnouncementPost) {
    allowedPostTypes = ["announcementReadOnly", "announcement"];
  } else if (canCreateStandardPost) {
    allowedPostTypes = ["undefined"];
  }

  let defaultPostType: MessageCreateRequest["type"] | undefined;
  if (useRedesignedForm) {
    defaultPostType = "announcementReadOnly";
  } else if (allowedPostTypes.length === 1) {
    defaultPostType = allowedPostTypes[0];
  }

  const defaultFormValues: AdminCreatePostFormValues = {
    title: "",
    content: "",
    contentTemplate: undefined,
    mentions: undefined,
    postType: defaultPostType,
    images: [],
    videos: [],
    documents: [],
    poll: undefined,
    audienceEntityId: undefined,
    audienceType: undefined,
    scheduleDate: undefined,
    isEmailPreviewOnly: true,
    hasDate: undefined,
    eventStartsAt: undefined,
    eventEndsAt: undefined,
    eventType: undefined,
    eventLocation: "",
    eventMaxParticipants: "",
    eventNoParticipantsLimit: true,
    eventAllowGuests: true,
  };

  const form = useForm<AdminCreatePostFormValues>({
    defaultValues: defaultFormValues,
  });
  const copilotHistory = usePostCopilotHistory((post) => {
    form.setValue("title", post.title, { shouldDirty: true, shouldValidate: true });
    form.setValue("content", post.content, { shouldDirty: true, shouldValidate: true });
    form.setValue("contentTemplate", post.contentTemplate, { shouldDirty: true });
    form.setValue("mentions", post.mentions, { shouldDirty: true });
  });

  const currPostType = useWatch({
    name: "postType",
    control: form.control,
  });
  const currTitle = useWatch({
    name: "title",
    control: form.control,
  });
  const currContent = useWatch({
    name: "content",
    control: form.control,
  });
  const currImages = useWatch({
    name: "images",
    control: form.control,
  });
  const currVideos = useWatch({
    name: "videos",
    control: form.control,
  });
  const currDocuments = useWatch({
    name: "documents",
    control: form.control,
  });
  const currAudienceEntityId = useWatch({
    name: "audienceEntityId",
    control: form.control,
  });
  const currAudienceType = useWatch({
    name: "audienceType",
    control: form.control,
  });
  const currScheduleDate = useWatch({
    name: "scheduleDate",
    control: form.control,
  });
  const currPoll = useWatch({
    name: "poll",
    control: form.control,
  });
  const currHasDate = useWatch({
    name: "hasDate",
    control: form.control,
  });
  const currEventStartsAt = useWatch({
    name: "eventStartsAt",
    control: form.control,
  });
  const currEventEndsAt = useWatch({
    name: "eventEndsAt",
    control: form.control,
  });
  const currEventType = useWatch({
    name: "eventType",
    control: form.control,
  });
  const currEventLocation = useWatch({
    name: "eventLocation",
    control: form.control,
  });
  const currEventMaxParticipants = useWatch({
    name: "eventMaxParticipants",
    control: form.control,
  });
  const currEventNoParticipantsLimit = useWatch({
    name: "eventNoParticipantsLimit",
    control: form.control,
  });
  const currEventAllowGuests = useWatch({
    name: "eventAllowGuests",
    control: form.control,
  });
  const { uploadFormImage } = useUploadImage();
  const { uploadFormVideo, abortUploadVideos } = useUploadVideo({});
  const { uploadFormDocument } = useUploadDocument();
  const addMessageMutation = communityFeedMutations.useAddMessage();
  const copilotResponseMutation = generalMutations.useGetCopilotResponse();
  const communityFeedQueries = useCommunityFeedQueries();
  const {
    data: audience,
    isPending: isPendingAudience,
    isError: isErrorAudience,
    error: errorAudience,
  } = useQuery(
    communityFeedQueries.audience({
      query: {
        Type: canCreateAnnouncementPost ? "announcement" : "post",
      },
    }),
  );

  const {
    data: templates,
    isPending: isPendingTemplates,
    isError: isErrorTemplates,
  } = useQuery(
    communityFeedQueries.listMessageTemplates({
      isPortfolio: false,
    }),
  );

  const getDefaultAudience: () => Pick<AdminCreatePostFormValues, "audienceEntityId" | "audienceType"> =
    useCallback(() => {
      if (!audience) {
        return {
          audienceType: undefined,
          audienceEntityId: undefined,
        };
      }

      if (locationState?.groupId) {
        return {
          audienceType: "group",
          audienceEntityId: locationState.groupId,
        };
      }

      if (audience.project) {
        return {
          audienceType: "project",
          audienceEntityId: audience.project.id,
        };
      }

      const audienceGroups = [...audience.realEstateGroups, ...audience.interestGroups];
      if (audienceGroups.length === 1) {
        return {
          audienceType: "group",
          audienceEntityId: audienceGroups[0].id,
        };
      }

      return {
        audienceType: undefined,
        audienceEntityId: undefined,
      };
    }, [audience, locationState]);

  // Set default selected audience
  useEffect(() => {
    if (!audience) {
      return;
    }

    const { audienceEntityId, audienceType } = getDefaultAudience();

    form.reset({
      ...form.getValues(),
      audienceEntityId,
      audienceType,
    });
  }, [audience, form, getDefaultAudience, locationState]);

  const reset = () => {
    const { audienceEntityId, audienceType } = getDefaultAudience();

    form.reset({
      ...defaultFormValues,
      audienceEntityId,
      audienceType,
    });
    copilotHistory.reset();
    setIsSubmitting(false);
    setTemplateInUse(undefined);
  };

  const handleGenerateContent = async () => {
    const original = {
      title: form.getValues("title"),
      content: form.getValues("content"),
      contentTemplate: form.getValues("contentTemplate"),
      mentions: form.getValues("mentions"),
    };
    const result = await copilotResponseMutation.mutateAsync({
      userInput: createMentionProtectedText(original),
      copilotOptions: [],
    });

    if (!result.ok || !result.body) {
      throw new Error("Failed to get copilot response");
    }

    let buffer = "";
    let generatedTitle = original.title;
    let generatedContent: string | undefined;
    let streamFailed = false;
    await processJsonStream<{ chunk: string }>({
      stream: result.body,
      callbacks: {
        onChunk: (result) => {
          buffer += result.chunk;

          const parsedJson = parseBestEffortJsonStream(buffer) as { title?: string; content?: string };
          if (typeof parsedJson?.title === "string" && parsedJson.title) {
            generatedTitle = parsedJson.title;
          }

          if (typeof parsedJson?.content === "string" && parsedJson.content) {
            generatedContent = parsedJson.content;
          }
        },
        onError: () => {
          streamFailed = true;
          showFlashToast({
            variant: "alert",
            message: t("page.admin-create-post.form.copilot.error"),
          });
        },
      },
    });
    if (streamFailed) {
      return;
    }
    const rewritten = generatedContent ? restoreMentionsAfterRewrite(generatedContent, original) : original;
    if (!rewritten) {
      showFlashToast({
        variant: "alert",
        message: t("page.admin-create-post.form.copilot.error"),
      });

      return;
    }
    const generated = {
      title: generatedTitle,
      content: rewritten.content,
      contentTemplate: rewritten.contentTemplate,
      mentions: rewritten.mentions,
    };
    copilotHistory.begin(original);
    form.setValue("title", generated.title, { shouldDirty: true, shouldValidate: true });
    form.setValue("content", generated.content, { shouldDirty: true, shouldValidate: true });
    form.setValue("contentTemplate", generated.contentTemplate, { shouldDirty: true });
    form.setValue("mentions", generated.mentions, { shouldDirty: true });
    copilotHistory.record(generated);
  };

  const handleSelectPostType = (newPostType: MessageCreateRequest["type"]) => {
    if (newPostType !== currPostType) {
      copilotHistory.reset();
    }

    form.setValue("postType", newPostType, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleSelectAudience = (newAudienceGroupId: string | undefined, newProjectConnectionId: string | undefined) => {
    let nextAudienceType: AdminPostAudienceType = "project";
    if (newAudienceGroupId) {
      nextAudienceType = "group";
    } else if (newProjectConnectionId) {
      nextAudienceType = "projectConnection";
    }

    const nextAudienceEntityId = newAudienceGroupId ?? newProjectConnectionId ?? audience?.project?.id;
    if (nextAudienceType !== currAudienceType || nextAudienceEntityId !== currAudienceEntityId) {
      copilotHistory.reset();
    }

    if (newAudienceGroupId) {
      form.setValue("audienceEntityId", newAudienceGroupId, {
        shouldDirty: currAudienceType !== "group" || newAudienceGroupId !== currAudienceEntityId,
      });

      if (currAudienceType !== "group") {
        form.setValue("audienceType", "group");
      }
    } else if (newProjectConnectionId) {
      form.setValue("audienceEntityId", newProjectConnectionId, {
        shouldDirty: currAudienceType !== "projectConnection" || newProjectConnectionId !== currAudienceEntityId,
      });

      if (currAudienceType !== "projectConnection") {
        form.setValue("audienceType", "projectConnection");
      }
    } else {
      form.setValue("audienceEntityId", audience?.project?.id);

      if (currAudienceType !== "project") {
        form.setValue("audienceType", "project");
      }
    }
  };

  const handleSelectScheduleDate = (date: Date | undefined) => {
    form.setValue("scheduleDate", date, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleApplyTemplate = ({
    template,
    title,
    content,
  }: {
    template: PostTemplateDto;
    title: string;
    content: string;
  }) => {
    const postType =
      allowedPostTypes.length > 1
        ? allowedPostTypes.find((allowedPostType) => allowedPostType === template.postType)
        : allowedPostTypes[0];

    form.reset({
      title,
      content,
      postType,
      images: currImages,
      videos: currVideos,
      documents: currDocuments,
      audienceEntityId: currAudienceEntityId,
      audienceType: currAudienceType,
      scheduleDate: currScheduleDate,
      hasDate: currHasDate,
      eventStartsAt: currEventStartsAt,
      eventEndsAt: currEventEndsAt,
      eventType: currEventType,
      eventLocation: currEventLocation,
      eventMaxParticipants: currEventMaxParticipants,
      eventNoParticipantsLimit: currEventNoParticipantsLimit,
      eventAllowGuests: currEventAllowGuests,
    });

    setTemplateInUse(template);
  };

  const handleClearTemplate = () => {
    form.reset({
      ...defaultFormValues,
      images: currImages,
      videos: currVideos,
      documents: currDocuments,
      audienceEntityId: currAudienceEntityId,
      audienceType: currAudienceType,
      scheduleDate: currScheduleDate,
    });

    setTemplateInUse(undefined);
  };

  const createCommunityPost = async ({
    formValues,
    imageIds,
    videoIds,
    documentIds,
  }: {
    formValues: AdminCreatePostFormValues;
    imageIds: string[];
    videoIds: string[];
    documentIds: string[];
  }) => {
    if (!formValues.postType) {
      return;
    }

    const pollEndDate = formValues.poll
      ? addDays(formValues.scheduleDate ?? new Date(), formValues.poll.durationInDays)
      : undefined;

    const data: MessageCreateRequest = {
      type: formValues.postType,
      title: formValues.title,
      content: formValues.content,
      contentTemplate: formValues.contentTemplate,
      mentions: formValues.mentions,
      isHiddenFromAdmins: false,
      imageIds,
      videoIds,
      documentIds,
      groupId: formValues.audienceType === "group" ? formValues.audienceEntityId : undefined,
      projectConnectionId: formValues.audienceType === "projectConnection" ? formValues.audienceEntityId : undefined,
      scheduledFor: formValues.scheduleDate ? formValues.scheduleDate.toISOString() : undefined,
      aiTemplateId: templateInUse?.id,
      isEmailPreviewOnly: formValues.isEmailPreviewOnly,
      poll:
        formValues.poll && pollEndDate
          ? {
              allowMultipleAnswers: formValues.poll.allowMultipleAnswers || false,
              endsAt: pollEndDate.toISOString(),
              answers: formValues.poll.options,
              question: formValues.poll.question,
            }
          : undefined,
      event: toCommunityPostEvent(formValues),
    };

    successModalHandler.setTrue();

    try {
      const createdMessage = await addMessageMutation.mutateAsync({
        data,
      });

      setCreatedMessage(createdMessage);
    } catch {
      successModalHandler.setFalse();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (formValues: AdminCreatePostFormValues) => {
    setIsSubmitting(true);

    const imageUploadPromises = formValues.images.map((image) => uploadFormImage(image));
    // Curently only 1 document is allowed
    const documentUploadPromises = formValues.documents.map((document) => uploadFormDocument(document));
    // Curently only 1 video is allowed
    const videoUploadPromises = formValues.videos.map((video) => uploadFormVideo(video));
    const imageIds = (await Promise.allSettled(imageUploadPromises))
      .map((image) => (image.status === "fulfilled" ? image.value?.id : ""))
      .filter(Boolean) as string[];
    const documentIds = (await Promise.allSettled(documentUploadPromises))
      .map((document) => (document.status === "fulfilled" ? document.value?.id : ""))
      .filter(Boolean) as string[];
    const videoIds = (await Promise.allSettled(videoUploadPromises))
      .map((video) => (video.status === "fulfilled" ? video.value?.id : ""))
      .filter(Boolean) as string[];

    // Only create message when all attachments were uploaded successfully
    if (
      imageIds.length === formValues.images.length &&
      documentIds.length === formValues.documents.length &&
      videoIds.length === formValues.videos.length
    ) {
      await createCommunityPost({
        formValues,
        imageIds,
        videoIds,
        documentIds,
      });
    } else {
      showFlashToast({
        variant: "alert",
        message: t("page.message-feed.create.notifications.error"),
      });
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Cancel all ongoing video uploads
    abortUploadVideos(form.getValues("videos"));

    void navigate(
      routes.communityFeed.list({
        slug,
      }),
    );
  };

  if (isPendingAudience || isPendingTemplates) {
    return <FullSizeLoader withPadding />;
  }

  // Error on templates should not block page usage
  if (isErrorAudience) {
    return <ErrorPage error={errorAudience} />;
  }

  const { isUndoable, isRedoable } = copilotHistory;
  const isCopilotAvailable = currContent.trim().split(" ").length >= 3;
  const allAudienceGroups = [...audience.realEstateGroups, ...audience.interestGroups];
  const currAudienceGroup =
    currAudienceType === "group" ? allAudienceGroups.find((group) => group.id === currAudienceEntityId) : undefined;
  const currAudienceProject = currAudienceType === "project" ? audience.project : undefined;
  const currAudienceProjectConnection =
    currAudienceType === "projectConnection"
      ? audience.projectConnections.find((connection) => connection.id === currAudienceEntityId)
      : undefined;

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={t("page.admin-create-post.title")}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: t("page.message-feed.title"),
                to: routes.communityFeed.list({
                  slug,
                }),
              },
              {
                name: t("page.admin-create-post.title"),
              },
            ]}
          />
        }
      >
        <PageGrid.Row>
          <PageGrid.Column size={useRedesignedForm ? "66%" : "75%"} isDesktopIndifferent>
            <Form.Root formMethods={form} onSubmit={handleSubmit}>
              {useRedesignedForm ? (
                <AdminCreatePostRedesignForm
                  templates={templates ?? []}
                  isErrorTemplates={isErrorTemplates}
                  templateInUse={templateInUse}
                  onApplyTemplate={handleApplyTemplate}
                  onClearTemplate={handleClearTemplate}
                  isUndoable={isUndoable}
                  isRedoable={isRedoable}
                  isCopilotAvailable={isCopilotAvailable}
                  isGenerating={copilotResponseMutation.isPending}
                  onGenerate={handleGenerateContent}
                  onUndo={copilotHistory.undo}
                  onRedo={copilotHistory.redo}
                  audience={audience}
                  selectedGroup={currAudienceGroup}
                  selectedProject={currAudienceProject}
                  selectedProjectConnection={currAudienceProjectConnection}
                  onSelectGroup={(group) => handleSelectAudience(group.id, undefined)}
                  onSelectProject={() => handleSelectAudience(undefined, undefined)}
                  onSelectProjectConnection={(connection) => handleSelectAudience(undefined, connection.id)}
                  selectedScheduleDate={currScheduleDate}
                  onSelectScheduleDate={handleSelectScheduleDate}
                  onCancel={cancelModalHandler.setTrue}
                  isSubmitting={isSubmitting}
                  canCreateEvent={canCreateEvent}
                  canConfigureComments={
                    allowedPostTypes.includes("announcement") && allowedPostTypes.includes("announcementReadOnly")
                  }
                />
              ) : (
                <Stack gap="lg">
                  {/* Templates */}
                  {!isErrorTemplates && (
                    <AdminCreatePostFormTemplateSelector templates={templates} onApplyTemplate={handleApplyTemplate} />
                  )}
                  {/* Post type */}
                  <SectionCard title={t("page.admin-create-post.section.step-1.title")} icon={<FileQuestion01Icon />}>
                    <AdminCreatePostFormTypeSelector
                      allowedTypes={allowedPostTypes}
                      selectedType={currPostType}
                      onSelectType={handleSelectPostType}
                    />
                  </SectionCard>
                  {/* Post content */}
                  <SectionCard
                    title={t("page.admin-create-post.section.step-2.title")}
                    renderAction={
                      templateInUse
                        ? () => (
                            <Button
                              size="sm"
                              icon={<XCloseIcon />}
                              variant="ghostPrimary"
                              onClick={handleClearTemplate}
                            >
                              {t("page.admin-create-post.section.step-2.btn.clear-template", {
                                templateName: templateInUse.name,
                              })}
                            </Button>
                          )
                        : undefined
                    }
                    icon={<PencilLineIcon />}
                  >
                    <AdminCreatePostFormAIHelper
                      isUndoable={isUndoable}
                      isRedoable={isRedoable}
                      isCopilotAvailable={isCopilotAvailable}
                      isGenerating={copilotResponseMutation.isPending}
                      onGenerate={handleGenerateContent}
                      onUndo={copilotHistory.undo}
                      onRedo={copilotHistory.redo}
                      isDisabled={!!templateInUse}
                    />
                    <AdminCreatePostFormEditor
                      mentionAudience={{ type: currAudienceType ?? "project", id: currAudienceEntityId }}
                    />
                    {/* App preview (Mobile) */}
                    <nav className="mt-space-md flex flex-wrap items-center gap-space-sm @md:hidden">
                      <Button icon={<MessageSmileSquareIcon />} variant="subtle" onClick={tipsModalHandler.setTrue}>
                        {t("page.admin-create-post.form.btn.show-tips")}
                      </Button>
                      <CreatePostAppPreview
                        title={currTitle}
                        content={currContent}
                        images={currImages}
                        documents={currDocuments}
                        videos={currVideos}
                        groupName={
                          currAudienceType === "group"
                            ? allAudienceGroups.find((group) => group.id === currAudienceEntityId)?.name
                            : undefined
                        }
                        postType={canCreateAnnouncementPost ? currPostType || "announcement" : "undefined"}
                        fallbackBackgroundImage={sessionUser.project.backgroundImage}
                      />
                    </nav>
                  </SectionCard>

                  {/* Post audience */}
                  <SectionCard icon={<Settings04Icon />} title={t("page.admin-create-post.section.step-3.title")}>
                    <AdminCreatePostFormAudienceSelector
                      audience={audience}
                      selectedGroup={currAudienceGroup}
                      onSelectGroup={(group) => handleSelectAudience(group.id, undefined)}
                      selectedProject={currAudienceProject}
                      selectedProjectConnection={currAudienceProjectConnection}
                      onSelectProject={() => handleSelectAudience(undefined, undefined)}
                      onSelectProjectConnection={(connection) => handleSelectAudience(undefined, connection.id)}
                      isDisabled={!currPostType}
                    />
                    <Inline gap="sm" align="center">
                      <FormCheckbox
                        label={t("page.admin-create-post.form.email-preview-only.label")}
                        name="isEmailPreviewOnly"
                      />
                      <Info tooltip={t("page.admin-create-post.form.email-preview-only.info")} />
                    </Inline>
                    <ActionBar
                      secondary={
                        <Button fullWidth variant="subtle" tone="error" onClick={cancelModalHandler.setTrue}>
                          {t("common.action.cancel")}
                        </Button>
                      }
                    >
                      <AdminCreatePostFormScheduleSelector
                        selectedDate={currScheduleDate}
                        onSelectDate={handleSelectScheduleDate}
                      />
                      <Button type="submit" isLoading={isSubmitting}>
                        {currScheduleDate
                          ? t("page.admin-create-post.form.btn.cta-schedule")
                          : t("page.admin-create-post.form.btn.cta-post")}
                      </Button>
                    </ActionBar>
                  </SectionCard>
                </Stack>
              )}
            </Form.Root>
          </PageGrid.Column>

          {/* App preview (Desktop) */}
          <PageGrid.Column size={useRedesignedForm ? "33%" : "25%"} isDesktopIndifferent>
            <aside className="sticky top-4 hidden w-full self-start @md:block">
              <SectionCard variant="outlined">
                <CreatePostAppPreview
                  title={currTitle}
                  content={currContent}
                  images={currImages}
                  documents={currDocuments}
                  videos={currVideos}
                  groupName={currAudienceGroup?.name}
                  poll={currPoll}
                  postType={canCreateAnnouncementPost ? currPostType || "announcement" : "undefined"}
                  fallbackBackgroundImage={sessionUser.project.backgroundImage}
                  event={
                    currHasDate === "yes"
                      ? {
                          type: currEventType,
                          startsAt: currEventStartsAt,
                          endsAt: currEventEndsAt,
                          location: currEventLocation,
                        }
                      : undefined
                  }
                />
                <footer className="mx-auto w-fit">
                  <Button icon={<MessageSmileSquareIcon />} variant="ghostPrimary" onClick={tipsModalHandler.setTrue}>
                    {t("page.admin-create-post.form.btn.show-tips")}
                  </Button>
                </footer>
              </SectionCard>
            </aside>
          </PageGrid.Column>
        </PageGrid.Row>
      </DocumentPage>

      {/* Modals */}
      <ConfirmModal
        tone="primary"
        data-testid="cancel-modal"
        title={t("page.admin-create-post.cancel-modal.title")}
        description={t("page.admin-create-post.cancel-modal.description")}
        isOpened={isCancelModalOpened}
        shouldCloseOnEsc
        onReject={cancelModalHandler.setFalse}
        onOpenChange={cancelModalHandler.set}
        rejectBtnProps={{
          text: t("common.action.cancel"),
        }}
        onResolve={handleCancel}
        resolveBtnProps={{
          text: t("common.action.delete"),
        }}
        isLoading={false}
      />
      <AdminCreatePostTipsModal isOpened={isTipsModalOpened} onOpenChange={tipsModalHandler.set} />
      {currScheduleDate && (
        <CommunityPostReachDetailsModal
          isOpened={isSuccessModalOpened}
          onOpenChange={(state) => {
            if (!state) {
              reset();
            }
            successModalHandler.set(state);
          }}
          reach={createdMessage?.reach}
          postId={currPoll ? createdMessage?.pollId : createdMessage?.id}
          status={CommunityPostStatus.JUST_SCHEDULED}
          scheduledDate={currScheduleDate.toISOString()}
          postType={currPoll ? "poll" : "message"}
        />
      )}
      {!currScheduleDate && (
        <CommunityPostReachDetailsModal
          isOpened={isSuccessModalOpened}
          onOpenChange={(state) => {
            if (!state) {
              reset();
            }
            successModalHandler.set(state);
          }}
          reach={createdMessage?.reach}
          postId={currPoll ? createdMessage?.pollId : createdMessage?.id}
          status={CommunityPostStatus.JUST_DELIVERED}
          postType={currPoll ? "poll" : "message"}
        />
      )}
    </>
  );
}
